import { isWeb } from "@memorang/helpers/platform";
import { type StyleProp, StyleSheet, type ViewStyle } from "react-native";
import { Dialog, IconButton, Modal, Portal } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { ToastProvider } from "../Toast";

type Props = {
	visible: boolean;
	handleClose?: () => void;
	children: React.ReactNode;
	maxWidth?: unknown;
	showCloseButton?: boolean;
	dismissable?: boolean;
	handleBack?: () => void;
	height?: `${number}%` | number;
	bottom?: number;
	minHeight?: number | `${number}%`;
	width?: number | `${number}%`;
	anchor?: "center" | "top";
	style?: StyleProp<ViewStyle>;
};

const TOP_ANCHOR_OFFSET = 64;

const DialogContent = ({
	children,
	handleBack,
	showCloseButton,
	handleClose,
}: Pick<
	Props,
	"children" | "handleBack" | "showCloseButton" | "handleClose"
>) => {
	return (
		<>
			<ToastProvider>{children}</ToastProvider>
			{handleBack && (
				<Box position="absolute" top={12} left={12}>
					<IconButton icon="arrow-left" onPress={handleBack} />
				</Box>
			)}
			{showCloseButton && (
				<Box position="absolute" top={7} right={12}>
					<IconButton icon="close" onPress={handleClose} />
				</Box>
			)}
		</>
	);
};

const DialogWrapper = ({
	visible,
	bottom,
	height,
	minHeight,
	width = "80%",
	showCloseButton,
	handleClose,
	handleBack,
	dismissable = true,
	maxWidth,
	anchor = "center",
	style,
	children,
}: Props) => {
	const theme = useAppTheme();

	if (anchor === "top") {
		return (
			<Portal>
				<Modal
					visible={visible}
					dismissable={dismissable}
					onDismiss={dismissable ? handleClose : undefined}
					style={{
						justifyContent: "flex-start",
						paddingTop: TOP_ANCHOR_OFFSET,
					}}
					contentContainerStyle={StyleSheet.flatten([
						{
							width,
							alignSelf: "center",
							height,
							minHeight,
							overflow: "hidden",
							maxHeight: isWeb ? "90%" : undefined,
							maxWidth,
							borderRadius: theme.roundness * 7,
							backgroundColor: theme.colors.surface,
							marginTop: 24,
						},
						theme.componentOverrides.dialog,
						style,
					])}
				>
					<DialogContent
						handleBack={handleBack}
						showCloseButton={showCloseButton}
						handleClose={handleClose}
					>
						{children}
					</DialogContent>
				</Modal>
			</Portal>
		);
	}

	return (
		<Portal>
			<Dialog
				visible={visible}
				dismissable={dismissable}
				onDismiss={dismissable ? handleClose : undefined}
				style={StyleSheet.flatten([
					{
						width,
						alignSelf: "center",
						bottom: bottom || 0,
						height,
						minHeight,
						overflow: "hidden",
						maxHeight: isWeb ? "90%" : undefined,
						maxWidth,
						backgroundColor: theme.colors.surface,
					},
					theme.componentOverrides.dialog,
					style,
				])}
			>
				<DialogContent
					handleBack={handleBack}
					showCloseButton={showCloseButton}
					handleClose={handleClose}
				>
					{children}
				</DialogContent>
			</Dialog>
		</Portal>
	);
};

export default DialogWrapper;
